import { Box, Button, Grid } from '@mui/material'
import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import EmptyTableRow from 'src/Component/EmptyTableRow'
import PaginatedTable, { HeadCellI, SortOrderI } from 'src/Component/PaginatedTable'
import SearchInput from 'src/Component/SearchInput'
import { AppDispatch } from 'src/store'
import { startPageLoader, stopPageLoader } from 'src/store/features/common/commonSlice'
import DataRow from './DataRow'
import UploadExerciseModal from './UploadExerciseModal'
import ConfirmationBox from 'src/Component/ConfirmationBox'
import {
  useDeleteAITrainerExerciseMutation,
  useGetAITrainerExerciseListQuery,
} from 'src/api/aiTrainerApi'
import { errorToast, successToast } from 'src/utils/toastService'
import ViewExerciseDetailsModal from './ViewExerciseDetatilsModal'
import EditAITrainerModal from './EditAITrainerModal'

const headCells: HeadCellI[] = [
  {
    id: 'id',
    isCheckBox: true,
    sortable: false,
    label: 'checkbox',
  },
  {
    id: 'name',
    sortable: true,
    label: 'Exercise Name',
  },
  {
    id: 'video',
    sortable: true,
    label: 'Videos',
  },
  {
    id: 'equipment_needed',
    sortable: true,
    label: 'Equipment Needed',
  },
  {
    id: 'time',
    sortable: true,
    label: 'Time',
  },
  {
    id: 'instruction',
    sortable: true,
    label: 'Instruction',
  },
  {
    id: 'id',
    sortable: false,
    label: 'Action',
    align: 'center',
  },
]

const AITrainer = () => {
  const [page, setPage] = useState<number>(1)
  const [openUploadNewModal, setOpenUploadNewModal] = useState(false)
  const [sortOrder, setSortOrder] = useState<SortOrderI>('asc')
  const [sortOrderBy, setSortOrderBy] = useState<string>('') // headCells[i].id
  const [aiTrainerExerciseDetailsModal, setAITrainerExerciseDetailsModal] = useState({
    id: 0,
    open: false,
  })

  const [aiTrainerExerciseDeleteConfirmation, setAITrainerExerciseDeleteConfirmation] = useState({
    id: 0,
    open: false,
  })

  const [selectedAITrainerExerciseIds, setSelectedAITrainerExerciseIds] = useState<number[]>([])
  const [search, setSearch] = useState<string>('')

  // hooks
  const {
    data: AI_TRAINER_EXERCISE_DATA_RESPONSE,
    isFetching,
    refetch,
  } = useGetAITrainerExerciseListQuery(
    {
      search,
      page,
      pageSize: 10,
      sortBy: (() => {
        if (sortOrder === 'asc') {
          return sortOrderBy
        } else {
          return `-${sortOrderBy}`
        }
      })(),
    },
    { refetchOnMountOrArgChange: true },
  )
  const [deleteAITrainerExercise, { isLoading }] = useDeleteAITrainerExerciseMutation()
  const dispatch = useDispatch<AppDispatch>()

  // functions
  const handlePageChange = (page: number) => {
    setPage(page)
  }

  const handleSorting = (_sortOrderBy: string) => {
    setSortOrder((prevState) => {
      return prevState === 'asc' ? 'desc' : 'asc'
    })
    setSortOrderBy(_sortOrderBy)
  }

  const handleAITrainerExerciseDetailsModalClose = () => {
    setAITrainerExerciseDetailsModal({
      id: 0,
      open: false,
    })
  }

  const handleViewDetailsClick = (id: number) => {
    setAITrainerExerciseDetailsModal({
      id: id,
      open: true,
    })
  }

  const handleCloseDeleteConfirmation = () => {
    setAITrainerExerciseDeleteConfirmation({
      id: 0,
      open: false,
    })
  }

  const handleOpenDeleteConfirmation = (id: number) => {
    setAITrainerExerciseDeleteConfirmation({
      id: id,
      open: true,
    })
  }

  const handleDeleteAITrainerExercise = () => {
    deleteAITrainerExercise(aiTrainerExerciseDeleteConfirmation.id)
      .unwrap()
      .then((res) => {
        console.log(res)
        successToast('Exercise deleted successfully.')
        refetch()
      })
      .catch((err) => {
        console.log(err)
        errorToast('Unable to delete exercise.')
      })
      .finally(() => {
        handleCloseDeleteConfirmation()
        handleEditAITrainerModalClose()
      })
  }

  const handleSelectAITrainerExercise = (id: number) => {
    if (selectedAITrainerExerciseIds.includes(id)) {
      setSelectedAITrainerExerciseIds((prevState) => {
        return prevState.filter((rowId) => rowId !== id)
      })
    } else {
      setSelectedAITrainerExerciseIds((prevState) => {
        return [...prevState, id]
      })
    }
  }

  const handleSelectAllClick = (checked: boolean) => {
    if (checked && AI_TRAINER_EXERCISE_DATA_RESPONSE?.results.length) {
      setSelectedAITrainerExerciseIds(
        AI_TRAINER_EXERCISE_DATA_RESPONSE.results.map((row) => row.id),
      )
    } else {
      setSelectedAITrainerExerciseIds([])
    }
  }

  const onUploadAITrainerExerciseSuccessCallback = () => {
    setOpenUploadNewModal(false)
    refetch()
  }

  const handleDeleteSelected = () => {
    Promise.all(selectedAITrainerExerciseIds.map((id) => deleteAITrainerExercise(id).unwrap()))
      .then((res) => {
        console.log(res)
        successToast('Selected Exercises deleted successfully.')
        refetch()
      })
      .catch((err) => {
        console.log(err)
        errorToast('Unable to delete selected Exercise.')
      })
  }

  const handleSearch = (value: string) => {
    setPage(1)
    setSearch(value)
  }

  // use effects
  useEffect(() => {
    if (isFetching || isLoading) {
      dispatch(startPageLoader())
    } else {
      dispatch(stopPageLoader())
    }
  }, [isFetching, isLoading])

  useEffect(() => {
    setSelectedAITrainerExerciseIds([])
  }, [AI_TRAINER_EXERCISE_DATA_RESPONSE])

  const [editAITrainerModal, setEditAITrainerModal] = useState({
    id: 0,
    open: false,
  })

  const handleEditAITrainerClick = (id: number) => {
    setEditAITrainerModal({
      id: id,
      open: true,
    })
  }

  const handleEditAITrainerModalClose = () => {
    setEditAITrainerModal({
      id: 0,
      open: false,
    })
  }

  const onEditAITrainerSuccessCallback = () => {
    handleEditAITrainerModalClose()
    refetch()
  }
  return (
    <Box className='pageContainer'>
      <Grid container>
        <Grid md={8} sm={12} xs={12}>
          {' '}
        </Grid>
        <Grid md={4} sm={12} xs={12}>
          <Box>
            <SearchInput hasDebounce onChange={handleSearch} />
          </Box>
        </Grid>
      </Grid>

      <Box mt={10} textAlign='right'>
        <Button
          variant='contained'
          color='primary'
          type='button'
          onClick={() => setOpenUploadNewModal(true)}
        >
          UPLOAD NEW
        </Button>
      </Box>

      <Box mt={2}>
        {AI_TRAINER_EXERCISE_DATA_RESPONSE && (
          <PaginatedTable
            title='Exercises'
            headCells={headCells}
            currentPage={page}
            onPageChange={handlePageChange}
            totalPages={Math.ceil(AI_TRAINER_EXERCISE_DATA_RESPONSE?.count / 10)}
            onSortClick={handleSorting}
            onSelectAllClick={handleSelectAllClick}
            isAllSelected={
              AI_TRAINER_EXERCISE_DATA_RESPONSE.results.length ===
                selectedAITrainerExerciseIds.length && selectedAITrainerExerciseIds.length !== 0
            }
            numSelected={selectedAITrainerExerciseIds.length}
            onDeleteSelected={handleDeleteSelected}
          >
            <>
              {AI_TRAINER_EXERCISE_DATA_RESPONSE?.results?.length === 0 && (
                <EmptyTableRow cellCount={headCells.length} />
              )}
              {AI_TRAINER_EXERCISE_DATA_RESPONSE?.results?.map((row) => {
                return (
                  <DataRow
                    key={row.id}
                    id={row.id}
                    name={row.name}
                    video={row.video}
                    equipmentNeeded={row.equipment_needed}
                    time={row.time}
                    instruction={row.instruction}
                    selected={selectedAITrainerExerciseIds.includes(row.id)}
                    onViewDetailsClick={handleViewDetailsClick}
                    onDeleteClick={handleOpenDeleteConfirmation}
                    onSelect={handleSelectAITrainerExercise}
                    onEditClick={handleEditAITrainerClick}
                  />
                )
              })}
            </>
          </PaginatedTable>
        )}
      </Box>

      <UploadExerciseModal
        open={openUploadNewModal}
        onClose={() => setOpenUploadNewModal(false)}
        successCallback={onUploadAITrainerExerciseSuccessCallback}
      />

      {!!aiTrainerExerciseDetailsModal.id && (
        <ViewExerciseDetailsModal
          id={aiTrainerExerciseDetailsModal.id}
          open={aiTrainerExerciseDetailsModal.open}
          onClose={handleAITrainerExerciseDetailsModalClose}
        />
      )}

      <ConfirmationBox
        title='Are you sure you want to Delete this exercise.'
        open={aiTrainerExerciseDeleteConfirmation.open}
        onReject={handleCloseDeleteConfirmation}
        onAccept={handleDeleteAITrainerExercise}
      />

      {!!editAITrainerModal.id && (
        <EditAITrainerModal
          id={editAITrainerModal.id}
          open={editAITrainerModal.open}
          onClose={handleEditAITrainerModalClose}
          successCallback={onEditAITrainerSuccessCallback}
          onDeleteClick={handleOpenDeleteConfirmation}
        />
      )}
    </Box>
  )
}

export default AITrainer
